<script setup lang="ts">
import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import { getAllSites, getSiteByID } from '@/services/core.service'
import { getChartsMarketData } from '@/services/eos'
import { getLMPThresholdAlerts } from '@/services/eos/alerts/lmp-alerts.service'
import { SiteType } from '@/types/core/Site'
import type IISORealTimeLMPThreshold from '@/types/eos/alerts/LMPThresholdAlert'
import { formatCurrency } from '@/util/formatters'
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  LogarithmicScale,
  PointElement,
  TimeSeriesScale,
  Title,
  Tooltip,
  type ChartOptions,
  type Plugin,
} from 'chart.js'
import 'chartjs-adapter-luxon'
import Annotation, { type LineAnnotationOptions } from 'chartjs-plugin-annotation'
import zoomPlugin from 'chartjs-plugin-zoom'
import { ElMessage, ElIcon } from 'element-plus'
import { QuestionFilled } from '@element-plus/icons-vue'
import { DateTime, Interval } from 'luxon'
import { computed, onMounted, reactive, ref } from 'vue'
import { Line } from 'vue-chartjs'
import { useIntervalFn } from '@vueuse/core'

const selectedSiteRef = ref<ISiteIdentifier>()
const timezone = computed(() => selectedSiteRef.value?.timezone ?? 'UTC')

// Chart scaling options
const useLogScale = ref<boolean>(false)
const adaptiveScaling = ref<boolean>(false)

ChartJS.register(
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  TimeSeriesScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Annotation,
  zoomPlugin
)

const chartData = reactive({
  datasets: [
    {
      label: 'RT LMP',
      backgroundColor: '#336699',
      borderColor: '#336699',
      pointBackgroundColor: (ctx: any) => {
        const point = ctx.raw
        return point?.isHighest ? '#ff4444' : '#336699'
      },
      pointBorderColor: (ctx: any) => {
        const point = ctx.raw
        return point?.isHighest ? '#ff4444' : '#336699'
      },
      pointBorderWidth: (ctx: any) => {
        const point = ctx.raw
        return point?.isHighest ? 3 : 0
      },
      pointRadius: (ctx: any) => {
        const point = ctx.raw
        return point?.isHighest ? 8 : 3
      },
      pointHoverRadius: (ctx: any) => {
        const point = ctx.raw
        return point?.isHighest ? 12 : 6
      },
      pointHoverBackgroundColor: (ctx: any) => {
        const point = ctx.raw
        return point?.isHighest ? '#ff6666' : '#336699'
      },
      pointHoverBorderColor: (ctx: any) => {
        const point = ctx.raw
        return point?.isHighest ? '#ff4444' : '#336699'
      },
      pointHoverBorderWidth: (ctx: any) => {
        const point = ctx.raw
        return point?.isHighest ? 3 : 0
      },
      borderWidth: 2,
      fill: false,
      tension: 0.1,
      data: [],
      parsing: {
        xAxisKey: 'measure_time',
        yAxisKey: 'rt_lmp',
      },
    },
    {
      label: 'DA LMP',
      backgroundColor: '#339966',
      borderColor: '#339966',
      pointBackgroundColor: (ctx: any) => {
        const point = ctx.raw
        return point?.isHighest ? '#ff4444' : '#339966'
      },
      pointBorderColor: (ctx: any) => {
        const point = ctx.raw
        return point?.isHighest ? '#ff4444' : '#339966'
      },
      pointBorderWidth: (ctx: any) => {
        const point = ctx.raw
        return point?.isHighest ? 3 : 0
      },
      pointRadius: (ctx: any) => {
        const point = ctx.raw
        return point?.isHighest ? 8 : 3
      },
      pointHoverRadius: (ctx: any) => {
        const point = ctx.raw
        return point?.isHighest ? 12 : 6
      },
      pointHoverBackgroundColor: (ctx: any) => {
        const point = ctx.raw
        return point?.isHighest ? '#ff6666' : '#339966'
      },
      pointHoverBorderColor: (ctx: any) => {
        const point = ctx.raw
        return point?.isHighest ? '#ff4444' : '#339966'
      },
      pointHoverBorderWidth: (ctx: any) => {
        const point = ctx.raw
        return point?.isHighest ? 3 : 0
      },
      borderWidth: 2,
      fill: false,
      tension: 0.1,
      data: [],
      parsing: {
        xAxisKey: 'measure_time',
        yAxisKey: 'da_lmp',
      },
    },
    {
      label: 'RT LMP Threshold',
      backgroundColor: '#993333',
      data: [],
    },
  ],
})

// Computed property to find the highest RT LMP value and its index
const highestRTLMPData = computed(() => {
  const rtData = chartData.datasets[0].data as any[]
  if (!rtData || rtData.length === 0) return { value: null, index: -1 }

  let maxValue = -Infinity
  let maxIndex = -1

  rtData.forEach((point, index) => {
    if (point.rt_lmp !== null && point.rt_lmp !== undefined && point.rt_lmp > maxValue) {
      maxValue = point.rt_lmp
      maxIndex = index
    }
  })

  return { value: maxValue === -Infinity ? null : maxValue, index: maxIndex }
})

// Computed property to find the highest DA LMP value and its index
const highestDALMPData = computed(() => {
  const daData = chartData.datasets[1].data as any[]
  if (!daData || daData.length === 0) return { value: null, index: -1 }

  let maxValue = -Infinity
  let maxIndex = -1

  daData.forEach((point, index) => {
    if (point.da_lmp !== null && point.da_lmp !== undefined && point.da_lmp > maxValue) {
      maxValue = point.da_lmp
      maxIndex = index
    }
  })

  return { value: maxValue === -Infinity ? null : maxValue, index: maxIndex }
})

// Function to apply highlighting to the highest RT LMP and DA LMP points
const applyHighlighting = () => {
  const rtData = chartData.datasets[0].data as any[]
  const daData = chartData.datasets[1].data as any[]

  if (!rtData || rtData.length === 0) return

  // Find the highest RT LMP value
  let maxRtValue = -Infinity
  let maxRtIndex = -1

  rtData.forEach((point, index) => {
    if (point.rt_lmp !== null && point.rt_lmp !== undefined && point.rt_lmp > maxRtValue) {
      maxRtValue = point.rt_lmp
      maxRtIndex = index
    }
  })

  // Find the highest DA LMP value
  let maxDaValue = -Infinity
  let maxDaIndex = -1

  if (daData && daData.length > 0) {
    daData.forEach((point, index) => {
      if (point.da_lmp !== null && point.da_lmp !== undefined && point.da_lmp > maxDaValue) {
        maxDaValue = point.da_lmp
        maxDaIndex = index
      }
    })
  }

  // Create new RT data array with highlighting information
  const highlightedRtData = rtData.map((point, index) => ({
    ...point,
    isHighest: index === maxRtIndex,
  }))

  // Create new DA data array with highlighting information
  const highlightedDaData = daData.map((point, index) => ({
    ...point,
    isHighest: index === maxDaIndex,
  }))

  // Update the datasets with highlighted data
  chartData.datasets[0].data = highlightedRtData as any
  chartData.datasets[1].data = highlightedDaData as any
}

const onAnnotationEnter = (ctx: any, event: any) => {
  if (!rtLMPThresholdAnnotationOptions?.label) return
  rtLMPThresholdAnnotationOptions.label.display = true
  rtLMPThresholdAnnotationOptions.label.position = `${(event.x / ctx.chart.chartArea.width) * 100}%`
  ctx.chart.update()
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const onAnnotationLeave = (ctx: any, event: any) => {
  if (!rtLMPThresholdAnnotationOptions?.label) return
  rtLMPThresholdAnnotationOptions.label.display = false
  ctx.chart.update()
}

const defaultLegendClickHandler = ChartJS.defaults.plugins.legend.onClick as any
const annotationLegendClickHandler = (event: any, legendItem: any, legend: any) => {
  if (legendItem.datasetIndex == 2) {
    rtLMPThresholdAnnotationOptions.display = !rtLMPThresholdAnnotationOptions.display
  }
  defaultLegendClickHandler(event, legendItem, legend)
}

const rtLMPThresholdAnnotationOptions = reactive<LineAnnotationOptions>({
  yMin: 0,
  yMax: 0,
  borderColor: '#993333',
  borderWidth: 4,
  display: true,
  label: {
    display: false,
    position: 'center',
    drawTime: 'afterDatasetsDraw',
    backgroundColor: '#993333',
    color: 'white',
    content: 'RT LMP Threshold',
  },
  enter: onAnnotationEnter,
  leave: onAnnotationLeave,
})

// Function to determine if we should use logarithmic scaling
const shouldUseLogScale = (data: any[]) => {
  if (!adaptiveScaling.value) return useLogScale.value

  const rtData = data.filter((d) => d.rt_lmp !== null && d.rt_lmp !== undefined)
  const daData = data.filter((d) => d.da_lmp !== null && d.da_lmp !== undefined)

  const allValues = [...rtData.map((d) => d.rt_lmp), ...daData.map((d) => d.da_lmp)]
  if (allValues.length === 0) return false

  const maxValue = Math.max(...allValues)
  const minValue = Math.min(...allValues)

  // Use log scale if there's a large range of values (ratio > 10)
  return maxValue / Math.max(minValue, 1) > 10
}

const chartOptions = computed<ChartOptions<'line'>>(() => {
  // Get current data to determine scaling
  const currentData = chartData.datasets[0].data as any[]
  const useLog = shouldUseLogScale(currentData)

  return {
    maintainAspectRatio: false,
    responsive: true,
    interaction: {
      mode: 'nearest',
      intersect: false,
    },
    scales: {
      x: {
        type: 'timeseries',
        time: {
          unit: 'hour',
          tooltipFormat: 'M/dd/yyyy HH:mm',
          displayFormats: {
            hour: 'M/dd/yyyy H:mm',
          },
        },
        adapters: {
          date: {
            setZone: true,
            zone: timezone.value,
          },
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
      },
      y: {
        type: useLog ? 'logarithmic' : 'linear',
        min: useLog ? 0.1 : undefined,
        ticks: {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          callback: (value, index, values) => {
            return formatCurrency(value as number)
          },
          maxTicksLimit: 8,
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
        ...(useLog && {
          afterBuildTicks: (axis: any) => {
            // Custom tick generation for log scale
            const ticks = []
            const min = Math.max(0.1, axis.min)
            const max = axis.max
            const logMin = Math.log10(min)
            const logMax = Math.log10(max)
            const tickCount = 8

            for (let i = 0; i <= tickCount; i++) {
              const logValue = logMin + (logMax - logMin) * (i / tickCount)
              const value = Math.pow(10, logValue)
              if (value >= min && value <= max) {
                ticks.push({ value })
              }
            }
            axis.ticks = ticks
          },
        }),
      },
    },
    plugins: {
      tooltip: {
        mode: 'nearest',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        filter: (tooltipItem: any) => {
          // Show tooltip for all datasets that have data
          return tooltipItem.parsed.y !== null && tooltipItem.parsed.y !== undefined
        },
        callbacks: {
          label: (ctx) => {
            if (ctx.parsed.y === null || ctx.parsed.y === undefined) {
              return ''
            }
            return `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}`
          },
        },
      },
      annotation: {
        annotations: [rtLMPThresholdAnnotationOptions],
      },
      legend: {
        position: 'top',
        align: 'end',
        onClick: annotationLegendClickHandler,
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
        },
      },
      zoom: {
        pan: {
          enabled: true,
          mode: 'x',
          modifierKey: 'ctrl',
        },
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true,
          },
          mode: 'x',
        },
        limits: {
          x: {
            min: 'original',
            max: 'original',
          },
          y: {
            min: 'original',
            max: 'original',
          },
        },
      },
    },
  }
})

const updateChart = async (startDate: Date, endDate: Date, site: ISiteIdentifier | undefined) => {
  if (!site || !startDate || !endDate) {
    cleanChart()
    return
  }

  const startISO = DateTime.fromJSDate(startDate).setZone(timezone.value, { keepLocalTime: true }).toISO()!
  const endISO = DateTime.fromJSDate(endDate).setZone(timezone.value, { keepLocalTime: true }).toISO()!

  try {
    loading.value = true

    const siteData = await getSiteByID(site.site_id!)
    if (!siteData.iso) {
      ElMessage.error('Site does not have an ISO configured')
      cleanChart()
      return
    }
    if (!siteData.commercial_pricing_node) {
      ElMessage.error('Site does not have a commercial pricing node configured')
      cleanChart()
      return
    }
    if (!siteData.timezone) {
      ElMessage.error('Site does not have a timezone configured')
      cleanChart()
      return
    }

    const results = await Promise.all([
      getLMPThresholdAlerts(siteData.commercial_pricing_node),
      getChartsMarketData(siteData.iso, siteData.commercial_pricing_node, startISO, endISO),
    ])
    const thresholds = results[0] as IISORealTimeLMPThreshold[]
    const rtData = results[1].rt_market_data
    const daData = results[1].da_market_data

    if (thresholds.length > 0) {
      // Get earliest created threshold
      const threshold = thresholds.reduce((a, b) => {
        return new Date(a.created_at) < new Date(b.created_at) ? a : b
      })
      rtLMPThresholdAnnotationOptions.yMin = threshold.lmp_threshold_value_per_mwh || 0
      rtLMPThresholdAnnotationOptions.yMax = threshold.lmp_threshold_value_per_mwh || 0
      if (rtLMPThresholdAnnotationOptions.label) {
        rtLMPThresholdAnnotationOptions.label.content = `RT LMP Threshold: $${threshold.lmp_threshold_value_per_mwh}`
      }
      rtLMPThresholdAnnotationOptions.display = true
    } else {
      rtLMPThresholdAnnotationOptions.display = false
    }

    chartData.datasets[0].data = rtData as any
    chartData.datasets[1].data = daData as any

    // Apply highlighting to the highest RT LMP point
    applyHighlighting()
  } catch (err: any) {
    ElMessage({
      type: 'error',
      message: err.message,
    })
  } finally {
    loading.value = false
  }
}

// Auto-update chart every 5 minutes
useIntervalFn(
  () => {
    if (selectedSiteRef.value && daterange.value)
      updateChart(daterange.value[0], daterange.value[1], selectedSiteRef.value)
  },
  5 * 60 * 1000
)

const cleanChart = () => {
  // Clean DA and RT datasets
  chartData.datasets[0].data = []
  chartData.datasets[1].data = []

  // Clean RT threshold annotation
  rtLMPThresholdAnnotationOptions.display = false
  rtLMPThresholdAnnotationOptions.yMin = 0
  rtLMPThresholdAnnotationOptions.yMax = 0
  if (rtLMPThresholdAnnotationOptions.label) {
    rtLMPThresholdAnnotationOptions.label.content = ''
  }
}

const initialStart = DateTime.fromJSDate(new Date()).setZone(timezone.value).startOf('day')
const initialEnd = initialStart.plus({ days: 1 })
const daterange = ref<[Date, Date]>([initialStart.toJSDate(), initialEnd.toJSDate()])
const calendarStartRange = ref<DateTime | null>(null)
const loading = ref<boolean>(false)

/**
 * Handles a calendar day click event.
 *
 * Stores the start of the range for use with `disabledDate`.
 * Resets the stored value when both start and end are selected.
 *
 * @param newValue the new selected date range.
 */
const handleCalendarChange = (newValue: Array<Date | null>) => {
  if (newValue[0] && newValue[1] == null) {
    calendarStartRange.value = DateTime.fromJSDate(newValue[0])
  } else {
    calendarStartRange.value = null
  }
}

/**
 * Disables future dates from selection.
 * Returns true if date should be disabled, false if not.
 *
 * A date is disabled if:
 * 1. The date + 1 day (handles DA LMP) is in the future.
 * 2. A start range is selected and the date falls outside of start +/- 30 days.
 *
 * @param date the date to check.
 */
const disabledDate = (date: Date) => {
  let outsideValidRange = false
  if (calendarStartRange.value) {
    const minDate = calendarStartRange.value.minus({ months: 1 })
    const maxDate = calendarStartRange.value.plus({ months: 1 })
    outsideValidRange = date.getTime() < minDate.toMillis() || date.getTime() > maxDate.toMillis()
  }

  return date.getTime() > DateTime.now().plus({ day: 1 }).toMillis() || outsideValidRange
}

/**
 * Handles a date range change from the Element Plus date-range picker.
 *
 * @param newValue [start, end] Date values supplied by the Element Plus date-range picker.
 */
const handleDateRangeChange = async (newRange: Array<Date> | null) => {
  if (!newRange) {
    return
  }

  const startDate = DateTime.fromJSDate(newRange[0])
  let endDate = DateTime.fromJSDate(newRange[1])

  const interval = Interval.fromDateTimes(startDate, endDate)
  if (interval.length() == 0) {
    // Add one day to API call param if the same date was selected twice.
    endDate = endDate.plus({ days: 1 })
  }

  if (startDate && endDate) {
    await updateChart(startDate.toJSDate(), endDate.toJSDate(), selectedSiteRef.value)
  }
}

onMounted(async () => {
  // Use the first available site as the default
  const availableSites = await getAllSites({ type: SiteType.DataCenter })
  if (availableSites.length > 0) {
    selectedSiteRef.value = {
      site_id: availableSites[0].site_id,
      site_name: availableSites[0].site_name,
      type: SiteType.DataCenter,
      timezone: availableSites[0].timezone,
    }
  }

  updateChart(initialStart.toJSDate(), initialEnd.toJSDate(), selectedSiteRef.value)
})

const onSiteChange = (site: ISiteIdentifier) => {
  if (!site) {
    cleanChart()
    selectedSiteRef.value = undefined
    return
  }

  selectedSiteRef.value = site
  updateChart(daterange.value[0], daterange.value[1], site)
}
</script>

<template>
  <el-card v-loading="loading" class="lmp-chart-card">
    <div class="flex flex-col gap-lg">
      <header class="w-full inline-flex justify-between items-center">
        <h3>Real Time LMP (5-minute intervals) - {{ timezone }}</h3>
        <div class="flex flex-row gap-2">
          <div class="w-64">
            <RcSiteSelect v-model="selectedSiteRef" :type="SiteType.DataCenter" @change="onSiteChange" />
          </div>
          <div class="flex-1">
            <el-date-picker
              v-model="daterange"
              :disabled="!selectedSiteRef || loading"
              type="daterange"
              start-placeholder="Start date"
              end-placeholder="End date"
              :disabled-date="disabledDate"
              range-separator="to"
              @change="handleDateRangeChange"
              @calendar-change="handleCalendarChange"
            />
          </div>
        </div>
      </header>

      <!-- Chart Controls -->
      <div class="flex flex-row gap-4 items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div class="flex items-center gap-2">
          <el-checkbox v-model="adaptiveScaling" label="Adaptive Scaling" />
          <el-tooltip
            content="Automatically switches between linear and logarithmic scaling based on data range"
            placement="top"
          >
            <el-icon><QuestionFilled /></el-icon>
          </el-tooltip>
        </div>
        <!-- Highest Values Display -->
        <div
          v-if="highestRTLMPData.value !== null || highestDALMPData.value !== null"
          class="flex items-center gap-4 ml-auto"
        >
          <div v-if="highestRTLMPData.value !== null" class="flex items-center gap-2">
            <div class="w-3 h-3 rounded-full bg-red-500 border-2 border-red-400"></div>
            <span class="text-sm font-medium"> Highest RT LMP: {{ formatCurrency(highestRTLMPData.value) }} </span>
          </div>
          <div v-if="highestDALMPData.value !== null" class="flex items-center gap-2">
            <div class="w-3 h-3 rounded-full bg-red-500 border-2 border-red-400"></div>
            <span class="text-sm font-medium"> Highest DA LMP: {{ formatCurrency(highestDALMPData.value) }} </span>
          </div>
        </div>
      </div>
      <div class="min-h-[600px]">
        <Line
          v-if="!loading && daterange && selectedSiteRef"
          :options="chartOptions"
          :data="chartData"
          :plugins="[Annotation as Plugin<'line'>, zoomPlugin as Plugin<'line'>]"
        />
      </div>
    </div>
  </el-card>
</template>
